import Editor, { type OnMount } from "@monaco-editor/react";
import { useEffect, useRef } from "react";

import { languageFor } from "../lib/api";
import { uriFor } from "../lib/lsp";

interface Props {
  path: string;
  value: string;
  /** Line to reveal, 1-based. Bumped by the app when a search result is clicked. */
  revealLine?: number | null;
  onChange: (value: string) => void;
  onSave: () => void;
}

export function CodeEditor({ path, value, revealLine, onChange, onSave }: Props) {
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !revealLine) return;
    editor.revealLineInCenter(revealLine);
    editor.setPosition({ lineNumber: revealLine, column: 1 });
    editor.focus();
  }, [revealLine, path]);

  return (
    <Editor
      className="editor-host"
      theme="vs-dark"
      // The model URI must match what the language server is told about, or
      // diagnostics arrive for a document Monaco does not believe it has open.
      path={uriFor(path)}
      language={languageFor(path)}
      value={value}
      onChange={(next) => onChange(next ?? "")}
      onMount={(editor, monaco) => {
        editorRef.current = editor;
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, onSave);
      }}
      options={{
        fontFamily: 'SF Mono, Menlo, Consolas, "Liberation Mono", monospace',
        fontSize: 14,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        renderWhitespace: "selection",
        tabSize: 4,
        automaticLayout: true,
      }}
    />
  );
}
